import myAxios from './axiositeachWrapper';
import Config from '../_config/config';

const BASE_PATH = Config.nodeSiteUrl + 'v1/academic/macro-planning';
const TIMECRAFT_INPUT_PATH = Config.nodeSiteUrl + 'v1/academic/timecraft-input';

export interface SchedulingConfig {
  sessionDurationMinutes?: number;
  minDaysBetween?: number;
  maxPerDay?: number;
  maxPerWeek?: number;
  preferredDays?: string[];
}

export interface MacroPlanConfig {
  id?: number;
  scheduleBoardId?: number;
  templateId?: number;
  courseId: number;
  instructorId: number;
  totalSessionsNeeded: number;
  sessionDurationMinutes: number;
  preferredDays?: string[];
  preferredTimes?: Array<{ start: string; end: string }>;
  minDaysBetween?: number;
  maxPerDay?: number;
  maxPerWeek?: number;
  preferredRooms?: number[];
  moduleCode?: string;
  moduleName?: string;
  instructorName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MacroPlanWeek {
  id?: number;
  macroPlanConfigId: number;
  weekStartDate: string; // 'YYYY-MM-DD'
  targetClasses: number;
  isHoliday?: boolean;
  notes?: string;
  actualClasses?: number;
  status?: 'pending' | 'incomplete' | 'complete' | 'exceeded';
}

export interface ComplianceData {
  id: number;
  courseId: number;
  moduleCode: string;
  moduleName: string;
  instructorName: string;
  totalSessionsNeeded: number;
  totalTarget: number;
  totalActual: number;
  missing: number;
  exceeded: number;
  status: 'incomplete' | 'complete' | 'exceeded';
  startDate: string;
  endDate: string;
  instructorId: number;
  hasViolations?: boolean;
  violations?: Array<{
    type: string;
    message: string;
    severity: 'warning' | 'info' | 'error';
  }>;
}

export interface ModuleSchedule {
  id: number;
  courseId: number;
  moduleCode: string;
  moduleName: string;
  instructorId: number;
  instructorName: string;
  instructors: any; // JSON array of instructors
  sessions: number;
  studentGroup: string;
  enrollments: number;
  startDate: string;
  endDate: string;
  lessonPlanId?: number | null;
  schedulingConfig?: SchedulingConfig | null;
}

export interface ViolationDetail {
  weekNumber: number;
  weekStartDate: string;
  violationType: string;
  message: string;
  severity: 'warning' | 'error' | 'info';
  details?: any;
}

export interface ViolationSummary {
  configId: number;
  totalViolations: number;
  violationsByType: Record<string, number>;
  violationsBySeverity: Record<string, number>;
  violations: ViolationDetail[];
}

class MacroPlanningService {
  private buildParams(boardId: number, templateId?: number): Record<string, number> {
    if (templateId) return { templateId };
    return { boardId };
  }

  async getConfigs(boardId: number, templateId?: number): Promise<MacroPlanConfig[]> {
    const response: any = await myAxios.get(`${BASE_PATH}/configs`, {
      params: this.buildParams(boardId, templateId),
    });
    return response.data.data || [];
  }

  async saveConfig(config: MacroPlanConfig): Promise<MacroPlanConfig> {
    const response: any = await myAxios.post(`${BASE_PATH}/configs`, config);
    return response.data.data;
  }

  async getWeeklyTargets(configId: number): Promise<MacroPlanWeek[]> {
    const response: any = await myAxios.get(`${BASE_PATH}/configs/${configId}/weeks`);
    return response.data.data || [];
  }

  async saveWeeklyTargets(configId: number, weeks: MacroPlanWeek[]): Promise<void> {
    await myAxios.post(`${BASE_PATH}/configs/${configId}/weeks`, { weeks });
  }

  async getCompliance(boardId: number, templateId?: number): Promise<ComplianceData[]> {
    const response: any = await myAxios.get(`${BASE_PATH}/compliance`, {
      params: this.buildParams(boardId, templateId),
    });
    return response.data.data || [];
  }

  async getModuleSchedules(boardId: number, templateId?: number): Promise<ModuleSchedule[]> {
    const response: any = await myAxios.get(`${BASE_PATH}/module-schedules`, {
      params: this.buildParams(boardId, templateId),
    });
    return response.data.data || [];
  }

  async autoSchedule(
    configId: number,
    weekId?: number,
    mode: 'single' | 'all' = 'all',
  ): Promise<{
    success: boolean;
    data: {
      created: any[];
      unassigned: any[];
      createdCount: number;
      unassignedCount: number;
    };
    warnings: string[];
    message: string;
  }> {
    const response: any = await myAxios.post(`${BASE_PATH}/auto-schedule`, {
      configId,
      weekId,
      mode,
    });
    return response.data;
  }

  async getUnassignedSlots(configId: number): Promise<{
    weekId: number;
    weekStartDate: string;
    targetCount: number;
    actualCount: number;
    unassignedCount: number;
    reason: string;
  }[]> {
    const response: any = await myAxios.get(`${BASE_PATH}/unassigned`, {
      params: { configId },
    });
    return response.data.data || [];
  }

  async getConfigViolations(configId: number): Promise<ViolationSummary> {
    const response: any = await myAxios.get(`${BASE_PATH}/configs/${configId}/violations`);
    return response.data.data;
  }

  async importFromLessonPlan(
    boardId: number,
    courseId: number,
    lessonPlanId: number,
    templateId?: number,
  ): Promise<{
    configId: number;
    totalSessions: number;
    sessionDurationMinutes: number;
    weeksCreated: number;
    weekSummary: Array<{ week: string; sessions: number; hours: string }>;
  }> {
    const response: any = await myAxios.post(`${BASE_PATH}/configs/from-lesson-plan`, {
      boardId: templateId ? undefined : boardId,
      templateId,
      courseId,
      lessonPlanId,
    });
    return response.data.data;
  }

  async autoPopulateTemplate(templateId: number): Promise<{
    success: boolean;
    imported: number;
    skipped: number;
    message: string;
  }> {
    const response: any = await myAxios.post(`${TIMECRAFT_INPUT_PATH}/auto-populate-template`, {
      templateId,
    });
    return response.data;
  }
}

export default new MacroPlanningService();
